/* eslint-disable max-len */
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Geometry, IntersectResult } from '../../../platform/geom/Geometry';
import { Line } from '../../../platform/geom/Line';
import { round } from '../../../platform/geom/Math';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import potpack from '../../../platform/geom/Potpack';
import { Rect } from '../../../platform/geom/Rect';
import { place } from '../../canvas/commands/OrderCommands';
import { Phys } from '../../canvas/entities/Phys';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Pen } from '../../canvas/layout/Pen';
import { PhysicalPlot, Plot, RectPlot } from '../../canvas/layout/Plot';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { Shape } from '../shapes/Shape';
import { Zone } from '../zones/Zone';
import { AutoLayout, AutoLayoutSubject } from './AutoLayout';
import { AutoLayoutEntry } from './AutoLayoutAssocs';
import { AutoLayoutIndex } from './AutoLayoutIndex';
import { AutoLayoutPotentialInsert, LayoutStrategy } from './LayoutStrategy';

export type FlexLayoutAxis = 'column' | 'row';
export type FlexLayoutAlign = 'start' | 'center' | 'end';
export type FlexLayoutJustify = 'start' | 'center' | 'end';

const anyOrFirst = (value: string, opts: string[]) => {
    return opts.includes(value) ? value : opts[0];
};

export type EnforcedFlexLayoutConfig = [FlexLayoutAxis, FlexLayoutAlign, FlexLayoutJustify, number];
export type FlexLayoutConfig = EnforcedFlexLayoutConfig | ['none', null, null, any];
export const FlexLayoutConfig = {
    from(input: any[]) {
        if (input[0] === 'flex' || input[0] === 'bar' || input.length > 4) {
            return FlexLayoutConfig.from(input.slice(1));
        }
        let [axis, align, justify, gap] = input;
        if (axis !== 'column' && axis !== 'row') {
            return ['none', null, null, null];
        }

        axis = anyOrFirst(axis, ['none', 'column', 'row']);
        align = axis !== 'none' ? anyOrFirst(align, ['start', 'center', 'end']) : null;
        justify = axis !== 'none' ? anyOrFirst(justify, ['start', 'center', 'end']) : null;
        gap = parseInt(gap) || 10;
        return [axis, align, justify, gap] as FlexLayoutConfig;
    }
};

export const FlexSorter = {

    sort(axis: FlexLayoutAxis, arr: Physical[]) {
        return arr.sort(this.createComparer(axis));
    },

    createComparer(axis: FlexLayoutAxis, sorterOverrides?: Map<Physical, number>) {

        const compareUsingOverrides = (a: Physical, b: Physical) => {
            const ai = sorterOverrides?.get(a);
            const bi = sorterOverrides?.get(b);
            if (ai !== undefined && bi !== undefined) {
                return ai - bi;
            }
            return 0;
        };

        const compareX = (ar: Rect, ac: Point, br: Rect, bc: Point) => {
            if (ac.x < br.left) {
                return -1;
            }
            else if (ac.x > br.right) {
                return 1;
            }
            else {
                if (ac.y < bc.y) return -1;
                if (ac.y > bc.y) return 1;
                return 0;
            }
        };

        const compareY = (ar: Rect, ac: Point, br: Rect, bc: Point) => {
            if (ac.y < br.top) {
                return -1;
            }
            else if (ac.y > br.bottom) {
                return 1;
            }
            else {
                if (ac.x < bc.x) return -1;
                if (ac.x > bc.x) return 1;
                return 0;
            }
        };

        return (a: Physical, b: Physical) => {
            let ar = a.bounds(RelativeTo.Model);
            let br = b.bounds(RelativeTo.Model);

            if (ar.height > br.height) ar = new Rect(ar.left, ar.top, ar.width, br.height);
            if (br.height > ar.height) br = new Rect(br.left, br.top, br.width, ar.height);

            const ac = ar.center();
            const bc = br.center();

            // Row
            if (axis === 'row') {
                return compareY(ar, ac, br, bc) || compareX(ar, ac, br, bc) || compareUsingOverrides(a, b);
            }
            else {
                return compareX(ar, ac, br, bc) || compareY(ar, ac, br, bc) || compareUsingOverrides(a, b);
            }
        };
    }
};

// ROW
// width = length
// height = span

// position = left
// offset = top

// COL
// width = span
// height = length

// position = top
// offset = left

class FlexLayoutPlot implements Plot {

    public static forAll(axis: FlexLayoutAxis, plots: Plot[]) {
        return plots.map(p => new FlexLayoutPlot(axis, p));
    }

    private constructor(private axis: FlexLayoutAxis, private inner: Plot) {
    }

    public length() {
        return (this.axis === 'row') ? this.width() : this.height();
    }

    public span() {
        return (this.axis === 'row') ? this.height() : this.width();
    }

    public position() {
        return (this.axis === 'row') ? this.left() : this.top();
    }

    public offset() {
        return (this.axis === 'row') ? this.top() : this.left();
    }

    public layoutAt(position: number, offset: number) {
        if (this.axis === 'row') {
            this.moveTo(position, offset);
        }
        else {
            this.moveTo(offset, position);
        }
    }

    public get(metaProp: string) {
        return this.inner.get(metaProp);
    }

    public set(metaProp: string, value: any): void {
        this.inner.set(metaProp, value);
    }

    public left(): number {
        return this.inner.left();
    }

    public top(): number {
        return this.inner.top();
    }

    public width(): number {
        return this.inner.width();
    }

    public height(): number {
        return this.inner.height();
    }

    public topLeft(): Point {
        return this.inner.topLeft();
    }

    public center(): Point {
        return this.inner.center();
    }

    public apply(): void {
        return this.inner.apply();
    }

    public centerTo(x: number, y: number): this {
        this.inner.centerTo(x, y);
        return this;
    }

    public moveTo(x: number, y: number): this {
        this.inner.moveTo(x, y);
        return this;
    }

    public moveBy(x: number, y: number): this {
        this.inner.moveBy(x, y);
        return this;
    }

    public intersects(another: Plot) {
        return this.inner.intersects(another);
    }

    public intersectPoints(another: Geometry): IntersectResult {
        return this.inner.intersectPoints(another);
    }

    public bounds(): Rect {
        return this.inner.bounds();
    }

    public toShapeInfo() {
        return this.inner.toShapeInfo();
    }

}

type FlexLayoutSpace = { readonly length: number, readonly span: number };
const FlexLayoutSpace = (area: Rect, axis: FlexLayoutAxis) => {
    return {
        length: axis === 'row' ? area.width : area.height,
        span: axis === 'row' ? area.height : area.width
    };
};

const layoutAxis = (space: FlexLayoutSpace, plots: FlexLayoutPlot[], gap: number) => {

    let position = 0;
    let offset = 0;

    let dimSpan = 0;

    const dims = [[]] as FlexLayoutPlot[][];

    for (const tm of plots) {

        if (position + tm.length() > space.length) {
            position = 0;
            offset = offset + dimSpan + gap;
            dimSpan = 0;
            dims.push([]);
        }

        tm.layoutAt(position, offset);
        position += tm.length() + gap;
        dimSpan = Math.max(dimSpan, tm.span());

        dims[dims.length - 1].push(tm);
    }

    return dims;
};

const layoutAlign = (space: FlexLayoutSpace, align: FlexLayoutAlign, plots: FlexLayoutPlot[], dims: FlexLayoutPlot[][], gap: number) => {

    // First, align the group within the space
    const totalSpan = Math.max(...dims[dims.length - 1].map(p => p.offset() + p.span()));
    const groupOffset =
        (align === 'start' ? 0 : null) ??
        (align === 'center' ? (space.span - totalSpan) / 2 : null) ??
        (align === 'end' ? (space.span - totalSpan) : null);

    // for (const p of plots) {

    //     const usedSpan = p.span();
    //     const freeSpan = totalSpan - usedSpan;

    //     const itemOffset =
    //         (align === 'start' ? 0 : null) ??
    //         (align === 'center' ? freeSpan / 2 : null) ??
    //         (align === 'end' ? freeSpan : null);

    //     p.layoutAt(p.position(), p.offset() + groupOffset + itemOffset);

    // }

    plots.forEach(x => x.layoutAt(x.position(), x.offset() + groupOffset));
};

const layoutJustify = (space: FlexLayoutSpace, justify: FlexLayoutJustify, plots: FlexLayoutPlot[], dims: FlexLayoutPlot[][], gap: number) => {

    // Within each group, position the elements based on the justification
    for (const group of dims) {

        const usedLength = group.reduce((acc, x) => acc + x.length(), Math.max(0, (gap * group.length) - gap));
        const freeLength = space.length - usedLength;

        const groupPosition =
            (justify === 'start' ? 0 : null) ??
            (justify === 'center' ? freeLength / 2 : null) ??
            (justify === 'end' ? freeLength : null);

        group.forEach(x => x.layoutAt(x.position() + groupPosition, x.offset()));

    }
};

const layout = (area: Rect, [axis, align, justify, gap]: EnforcedFlexLayoutConfig, plots: Plot[]): void => {
    const space = FlexLayoutSpace(area.inflate(0), axis);
    const flexPlots = FlexLayoutPlot.forAll(axis, plots);
    const dims = layoutAxis(space, flexPlots, gap ?? 10);
    layoutAlign(space, align, flexPlots, dims, gap ?? 10);
    layoutJustify(space, justify, flexPlots, dims, gap ?? 10);

    // Crudely deal with overflow;
    const fb = Rect.fromMany(flexPlots.map(x => x.bounds()));
    const scale = new Point(
        Math.min(1, round(area.width / fb.width, 6)),
        Math.min(1, round(area.height / fb.height, 6)),
    );

    if (scale.x < 1) {
        for (const p of flexPlots) {
            const oc = Point.vector(fb.topLeft(), p.center());
            const ocp = oc.divide(fb.size());
            const nc = area.size().multiply(ocp);
            p.centerTo(nc.x, p.center().y);
        }
    }
    else if (scale.y < 1) {
        for (const p of flexPlots) {
            const oc = Point.vector(fb.topLeft(), p.center());
            const ocp = oc.divide(fb.size());
            const nc = area.size().multiply(ocp);
            p.centerTo(p.center().x, nc.y);
        }
    }

    // if (min < 0) {
    //     layout(area, [axis, align, justify, min], plots);
    // }
    // else {
    Pen.for(flexPlots)
        .moveBy(area.left, area.top);
    // }

};

type RefreshOpts = {
    cells?: number[],
    sorterOverrides?: Map<Physical, number>,
    tweenFromOverrides?: Map<Physical, Matrix>,
};

export class FlexLayout implements LayoutStrategy {

    public static layoutRects(area: Rect, [axis, align, justify, gap]: EnforcedFlexLayoutConfig, rects: Rect[]) {
        const plots = RectPlot.forAll(rects);
        layout(area, [axis, align, justify, gap], plots);
        return plots.map(x => x.bounds());
    }

    public static exec(area: Rect, [axis, align, justify, gap]: EnforcedFlexLayoutConfig, elmts: Physical[]) {
        const plots = PhysicalPlot.forAll(elmts);
        layout(area, [axis, align, justify, gap], plots);
        plots.forEach(x => x.apply());
    }

    public static row(gap: number, elmts: Physical[]) {
        const maxHeight = Math.max(...elmts.map(x => x.size().h));
        return FlexLayout.exec(new Rect(0, 0, 999999999, maxHeight), ['row', 'start', 'start', gap], elmts);
    }

    public static column(gap: number, elmts: Physical[]) {
        const maxWidth = Math.max(...elmts.map(x => x.size().w));
        return FlexLayout.exec(new Rect(0, 0, maxWidth, 999999999), ['column', 'start', 'start', gap], elmts);
    }

    public static portrait(config: EnforcedFlexLayoutConfig, elmts: Physical[]) {

        const boxes = elmts.map(x => x.bounds(RelativeTo.Model)).map(x => ({
            x: x.left,
            y: x.top,
            w: x.width,
            h: x.height,
        }));

        const area = potpack(boxes);
        return FlexLayout.exec(new Rect(0, 0, area.w, area.h), config, elmts);
    }

    constructor(
        private readonly dispatch: CommandDispatcher,
        private readonly effects: EffectDirector,
        private readonly tracker: AutoLayoutIndex,
        private readonly spatial: SpatialIndex) { }

    public queryPotentialInsert(container: AutoLayout, mpt: Point, elements: ReadonlySet<Physical>): AutoLayoutPotentialInsert | null {

        const [axis, align, justify] = FlexLayoutConfig.from(container.layoutMode);
        if (axis !== 'row' && axis !== 'column') return null;

        const targetCell = container.layoutCells(RelativeTo.Model).find(x => x.contains(mpt));
        if (!targetCell) return null;

        // Find elements inside the cell that are actively managed by the layout engine, then find the nearest one to the mouse position
        const managedElmts = this.tracker.findElements(container);
        const nearest = Phys.from(this.spatial.lookup(targetCell))
            .filter(x => managedElmts.has(x) && !elements.has(x))
            .filter(x => targetCell.contains(x.center(RelativeTo.Model)))
            .nearestTo(mpt);

        const relativeGuide = axis === 'row'
            ? new Line(new Point(0, -32), new Point(0, 32))
            : new Line(new Point(-32, 0), new Point(32, 0));

        // If no nearest, return position of appropriate side depending on axis
        if (!!nearest) {
            const nb = nearest.bounds(RelativeTo.Model).inflate(5);
            const nc = nearest.center(RelativeTo.Model);
            const at =
                (axis === 'row' && mpt.x < nc.x ? nb.pointAt(0, .5) : null) ??
                (axis === 'row' && mpt.x > nc.x ? nb.pointAt(1, .5) : null) ??
                (axis === 'column' && mpt.y < nc.y ? nb.pointAt(.5, 0) : null) ??
                (axis === 'column' && mpt.y > nc.y ? nb.pointAt(.5, 1) : null);

            const guides = [relativeGuide.translate(at)];
            return { container, at, guides };
        }
        else {
            const elmtSizes = Array.from(elements).map(e => e.size()).filter(s => s.x > 0 && s.y > 0);

            if (axis === 'column') {
                const x =
                    (align === 'start' ? 0 : null) ??
                    (align === 'end' ? 1 : null) ??
                    .5;
                const y =
                    (justify === 'start' ? 0 : null) ??
                    (justify === 'end' ? 1 : null) ??
                    .5;

                const offset = Math.min(...elmtSizes.map(s => s.x)) / 2;
                const at = this.computeCorrectedCellArea(container, targetCell)
                    .inflate(-10)
                    .inflate([-offset, 0])
                    .pointAt(x, y);

                const guides = [relativeGuide.translate(at)];
                return { container, at, guides };
            }
            else {
                const y =
                    (align === 'start' ? 0 : null) ??
                    (align === 'end' ? 1 : null) ??
                    .5;
                const x =
                    (justify === 'start' ? 0 : null) ??
                    (justify === 'end' ? 1 : null) ??
                    .5;

                const offset = Math.min(...elmtSizes.map(s => s.y)) / 2;
                const at = this.computeCorrectedCellArea(container, targetCell)
                    .inflate(-10)
                    .inflate([0, -offset])
                    .pointAt(x, y);

                const guides = [relativeGuide.translate(at)];
                return { container, at, guides };
            }
        }
    }

    public insert(container: AutoLayout, mpt: Point, elements: Iterable<Physical>) {

        const [axis] = FlexLayoutConfig.from(container.layoutMode);
        if (axis !== 'row' && axis !== 'column') return null;

        const cells = container.layoutCells(RelativeTo.Model);
        const cellIndex = cells.findIndex(x => x.contains(mpt));
        if (cellIndex < 0) return;

        const tweenFromOverrides = new Map<Physical, Matrix>();

        // Basically, pile the elements on top of each other (using center) at the point
        const sorted = Array.from(elements).sort(FlexSorter.createComparer(axis)).filter(x => !x.is(Shape) && !x.is(Zone));
        const sorterOverrides = new Map(sorted.map((x, i) => [x, i]));

        sorted.forEach((elmt, i) => {
            tweenFromOverrides.set(elmt, elmt.transform(RelativeTo.Model));
            const mv = Point.vector(elmt.center(RelativeTo.Model), mpt);
            elmt.moveBy(mv);
            this.spatial.invalidate(elmt);
        });

        this.refreshInternal(container, {
            cells: [cellIndex],
            sorterOverrides,
            tweenFromOverrides,
        });
    }

    public refresh(container: AutoLayout) {
        this.refreshInternal(container);
    }

    private refreshInternal(container: AutoLayout, opts: RefreshOpts = {}) {

        const [axis, align, justify, gap] = FlexLayoutConfig.from(container.layoutMode);
        if (axis !== 'row' && axis !== 'column') return null;

        const cells = container.layoutCells(RelativeTo.Model);
        const touched = [] as Physical[];

        for (let ci = 0; ci < cells.length; ci++) {
            if (opts.cells && !opts.cells.includes(ci)) continue;

            const area = cells[ci];
            const availableArea = this.computeCorrectedCellArea(container, area);

            // For each cell of the layout, find the head physicals that are in front of the container
            // and who's center is within the cell bounds
            const results = this.spatial.lookup(area);
            const elmts = Phys.from(results)
                .whereAbove(container)
                .polarize(AutoLayoutSubject)
                .takeHeads()
                .filter(x => area.contains(x.center(RelativeTo.Model)))
                .toArray()
                .sort(FlexSorter.createComparer(axis, opts.sorterOverrides));

            const plots = PhysicalPlot.forAll(elmts);
            layout(availableArea, [axis, align, justify, gap], plots);
            plots.forEach((x, pi) => {
                const from = opts.tweenFromOverrides?.get(x.subject) ?? x.subject.transform(RelativeTo.Model);
                x.apply();
                const to = x.subject.transform(RelativeTo.Model);
                if (!from.equals(to)) {
                    this.effects.trigger(Effects.tween(from, to), x.subject);
                }
                AutoLayoutEntry.setMaybe(x.subject, {
                    ownerId: container.id,
                    cell: ci,
                    index: pi,
                });
                touched.push(x.subject);
            });
        }

        // Remove any elements that are not in the container any more
        const managed = this.tracker.findElements(container);
        const cb = container.bounds(RelativeTo.Model);
        for (const me of managed) {
            if (cb.contains(me.center(RelativeTo.Model))) continue;
            AutoLayoutEntry.set(me, null);
        }

        // Place those that have moved
        if (touched.length > 0) {
            this.dispatch(place, [touched.map(x => x.id)]);
        }
    }

    private computeCorrectedCellArea(container: Physical, cell: Rect) {

        // const rects = Phys
        //     .from(this.spatial.lookup(cell))
        //     .whereAbove(container)
        //     .filter(x => x !== container && (x.is(Shape) || x.is(Zone)))
        //     .map(x => x.bounds(RelativeTo.Model));

        // const corrected = rects.reduce((t, x) => this.computeLargestFreeSpace(t, x), cell);
        // const larger = rects.find(x => x.area() > corrected.area());

        // return larger ? larger : corrected;
        return cell.inflate(-10);
    }

    private computeLargestFreeSpace(main: Rect, sub: Rect) {
        if (sub.intersects(main)) {
            const top = Rect.fromEdges(main.left, main.top, main.right, sub.top);
            const left = Rect.fromEdges(main.left, main.top, sub.left, main.bottom);
            const right = Rect.fromEdges(sub.right, main.top, main.right, main.bottom);
            const bottom = Rect.fromEdges(main.left, sub.bottom, main.right, main.bottom);
            const all = [top, left, right, bottom];
            return all.reduce((a, b) => a.area() > b.area() ? a : b);
        }
        else {
            return main.area() > sub.area() ? main : sub;
        }
    }
}
